import {
  closeHandoverSchema,
  closeNightlyStatusSchema,
  closeRunStartedSchema,
  IMPORT_ANOMALY_PAGE_DEFAULT,
  importAnomalyPageSchema,
  importRunDetailSchema,
  importRunListSchema,
  type CloseNightlyStatus,
  type CloseSavedSearchDecision,
} from "@gioia365/contracts";
import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../api/http";
import { commercialViewOf, tabKey, type TabContext } from "../session/tab-context";

// Le letture della pagina «Importazione da Close» (piano M2 Task 21, rotte del Task 15), con le chiavi dell'azienda della
// scheda (`["scheda", tabKey(tab), "imports", "close", …]`), tolte tutte insieme al cambio di azienda. Le scritture sono quelle
// di «Ogni sera da Close» (INGR-12, 18/10/2026), del solo Super admin di Gioia365: chiave, prova senza salvare, importazione e
// interruttore della sera; dopo un avvio l'elenco si rilegge. Mentre un'esecuzione è in corso
// l'elenco e il suo rapporto si rileggono ogni 5 secondi (proposta 5 del 1° ottobre 2026), e smettono quando finisce.
// I casi da controllare portano il loro Lead soltanto se chi guarda lo vede: le chiavi portano anche la Vista Commerciale.

/** Ogni quanto si rilegge un'esecuzione in corso. */
export const CLOSE_IMPORT_REFRESH_MS = 5_000;

const base = (tab: TabContext) => ["scheda", tabKey(tab), "imports", "close", commercialViewOf(tab)] as const;

/** Le esecuzioni dalla più recente (al massimo `IMPORT_RUNS_MAX`). */
export function closeImportRunsQuery(tab: TabContext) {
  return queryOptions({
    queryKey: [...base(tab), "runs"],
    queryFn: async ({ signal }) => (await apiRequest("GET", "/api/imports/close/runs", { schema: importRunListSchema, tab, signal })).runs,
    refetchInterval: (query) => (query.state.data?.some((run) => run.status === "running") ? CLOSE_IMPORT_REFRESH_MS : false),
  });
}

/** Un'esecuzione con il suo rapporto, che si rilegge finché è in corso. */
export function closeImportRunQuery(tab: TabContext, runId: string) {
  return queryOptions({
    queryKey: [...base(tab), "run", runId],
    queryFn: ({ signal }) => apiRequest("GET", `/api/imports/close/runs/${encodeURIComponent(runId)}`, { schema: importRunDetailSchema, tab, signal }),
    refetchInterval: (query) => (query.state.data?.run.status === "running" ? CLOSE_IMPORT_REFRESH_MS : false),
  });
}

/** I casi di un gruppo di «Da controllare», dieci alla volta («Mostra altri 10»). */
export function closeImportAnomaliesQuery(tab: TabContext, runId: string, code: string) {
  return infiniteQueryOptions({
    queryKey: [...base(tab), "run", runId, "anomalies", code],
    queryFn: ({ pageParam, signal }) => {
      const query = new URLSearchParams({ code, offset: String(pageParam), limit: String(IMPORT_ANOMALY_PAGE_DEFAULT) });
      return apiRequest("GET", `/api/imports/close/runs/${encodeURIComponent(runId)}/anomalies?${query.toString()}`, { schema: importAnomalyPageSchema, tab, signal });
    },
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0);
      return last.items.length > 0 && loaded < last.total ? loaded : undefined;
    },
    // Il rapporto di un'esecuzione finita non cambia: lo stesso gruppo riaperto non si rilegge subito.
    staleTime: 60_000,
  });
}

/** «Ogni sera da Close»: lo stato della chiave, della prova senza salvare e della sera; si rilegge ogni 5 secondi soltanto mentre un'importazione è viva. */
export function closeNightlyQuery(tab: TabContext) {
  return queryOptions({
    queryKey: [...base(tab), "nightly"],
    queryFn: ({ signal }) => apiRequest("GET", "/api/imports/close/nightly", { schema: closeNightlyStatusSchema, tab, signal }),
    refetchInterval: (query) => (query.state.data?.busy === true ? CLOSE_IMPORT_REFRESH_MS : false),
  });
}

/** Per quanto la verifica del passaggio vale senza rileggerla: conta tutti i record di Close dell'azienda, non si rifà a ogni ritorno. */
export const CLOSE_HANDOVER_STALE_MS = 5 * 60_000;

/** «Pronti a spegnere Close?» (M8): la verifica del passaggio, letta soltanto dal Super admin di Gioia365, al più ogni 5 minuti. */
export function closeHandoverQuery(tab: TabContext) {
  return queryOptions({
    queryKey: [...base(tab), "handover"],
    queryFn: ({ signal }) => apiRequest("GET", "/api/imports/close/handover", { schema: closeHandoverSchema, tab, signal }),
    staleTime: CLOSE_HANDOVER_STALE_MS,
    refetchOnWindowFocus: false,
  });
}

/**
 * La decisione su una ricerca salvata di Close che non è diventata una Smart List (23/10/2026, INGR-28): «Rifatta a mano», «Lasciata
 * perdere» o, con `null`, «Annulla». Il server risponde con la verifica nuova, che prende il posto di quella letta.
 */
export function useCloseSavedSearchDecision(tab: TabContext) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: CloseSavedSearchDecision | null }) => {
      const path = `/api/imports/close/handover/saved-searches/${encodeURIComponent(id)}`;
      return decision === null ? apiRequest("DELETE", path, { schema: closeHandoverSchema, tab }) : apiRequest("PUT", path, { schema: closeHandoverSchema, body: { decision }, tab });
    },
    onSuccess: (handover) => queryClient.setQueryData(closeHandoverQuery(tab).queryKey, handover),
  });
}

/** Le azioni di «Ogni sera da Close»; ognuna rimette lo stato che il server risponde e, dopo un avvio, rilegge l'elenco. */
export function useCloseNightlyActions(tab: TabContext) {
  const queryClient = useQueryClient();
  const settle = (status: CloseNightlyStatus) => queryClient.setQueryData(closeNightlyQuery(tab).queryKey, status);
  const runsChanged = () => queryClient.invalidateQueries({ queryKey: [...base(tab), "runs"] });
  const saveKey = useMutation({
    mutationFn: (apiKey: string) => apiRequest("PUT", "/api/imports/close/nightly/key", { schema: closeNightlyStatusSchema, body: { apiKey }, tab }),
    onSuccess: settle,
  });
  const removeKey = useMutation({
    mutationFn: () => apiRequest("DELETE", "/api/imports/close/nightly/key", { schema: closeNightlyStatusSchema, tab }),
    onSuccess: settle,
  });
  const schedule = useMutation({
    mutationFn: (enabled: boolean) => apiRequest("PUT", "/api/imports/close/nightly/schedule", { schema: closeNightlyStatusSchema, body: { enabled }, tab }),
    onSuccess: settle,
  });
  const start = useMutation({
    mutationFn: (what: "dry-run" | "import") => apiRequest("POST", `/api/imports/close/nightly/${what}`, { schema: closeRunStartedSchema, tab }),
    onSuccess: (started) => {
      settle(started.status);
      void runsChanged();
    },
  });
  return { saveKey, removeKey, schedule, start };
}
